import { zodResolver } from '@hookform/resolvers/zod';
import { JSX, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Form } from '@/components/ui/form';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { S3FileUploader } from '@/modules/common';
import { useTableStore } from '@/modules/common/store';

import {
  EventPrinterStatus,
  EventStatusEnum,
  EventTabsType,
} from '../constants';
import { getModelRequest } from '../helpers';
import { EventDetails, EventUploadUrlsData } from '../types';
import { EventFormSchema } from '../validations';
import { AiConfiguration } from './ai-configuration';
import { CreateEventAlertDialog } from './create-event-alert-dialog';
import { EventInfo } from './event-info';
import { Inventory } from './inventory';
import { MaskSelection } from './masks-selection';
import { ModelsSelection } from './models-selection';
import { Participants } from './participants';
import { PrintersSelection } from './printers-selection';
import { SecretKeys } from './secret-keys';
import { StylesSelection } from './styles-selection';
import { VideoPreview } from './video-preview';

export type EventFormSchemaType = z.infer<typeof EventFormSchema>;

interface EventFormProps {
  editMode?: boolean;
  eventDetails?: EventDetails;
  defaultValues?: EventFormSchemaType;
  uploadUrlData?: EventUploadUrlsData | null;
  refetchUploadCredentials: () => Promise<void>;
  onSubmit: (data: EventFormSchemaType) => Promise<void>;
  children: (props: { isLoading: boolean }) => JSX.Element;
}

export const defaultFormValues = {
  name: '',
  startDate: '',
  aiModel: '',
  iosSecretKey: '',
  desktopSecretKey: '',
  gptEnabled: false,
  status: EventStatusEnum.Active,
  flagsAndInitialsEnabled: false,
  electricEffectEnabled: false,
  placementFeatureEnabled: false,
  models: [
    {
      modelId: '',
    },
  ],
  styles: [
    {
      styleId: '',
    },
  ],
  masks: [
    {
      maskId: '',
      overlayId: '',
    },
  ],
  printers: [
    {
      name: '',
      printerId: '',
      type: '',
      status: EventPrinterStatus.Enabled,
    },
  ],
};

export function EventForm({
  onSubmit,
  children,
  eventDetails,
  uploadUrlData,
  defaultValues,
  editMode = false,
  refetchUploadCredentials,
}: EventFormProps) {
  const { resetTableState } = useTableStore();
  const [shouldOpenWarningDialog, setShouldOpenWarningDialog] = useState(false);
  const [isSubmitWarningDialogOpen, setIsSubmitWarningDialogOpen] =
    useState(false);
  const [activeTab, setActiveTab] = useState<EventTabsType>(
    EventTabsType.Models
  );

  const form = useForm<EventFormSchemaType>({
    resolver: zodResolver(EventFormSchema),
    defaultValues: defaultValues || defaultFormValues,
  });

  const handleFormSubmit = async (skipGcrValidation?: boolean) => {
    await form.trigger();

    const formErrors = form.formState.errors;

    if (Object.keys(formErrors).length > 0) {
      return;
    }

    if (shouldOpenWarningDialog && !skipGcrValidation) {
      setIsSubmitWarningDialogOpen(true);
      return;
    }

    const data = form.getValues();

    await onSubmit({
      ...data,
      models: getModelRequest(data.models),
    });
  };

  const filesConfig = [
    {
      key: 'publicUrl',
      uploadUrl: uploadUrlData?.signedUrl,
      fileUrl: defaultValues?.publicUrl,
      fileObject: eventDetails?.videoFileObject,
    },
    {
      key: 'idleStateVideoPublicUrl',
      uploadUrl: uploadUrlData?.idleStateVideoPublicUrl,
      fileUrl: defaultValues?.idleStateVideoPublicUrl,
      fileObject: eventDetails?.idleStateVideoFileObject,
    },
  ];

  const checkTabError = useCallback(
    (type: EventTabsType) => form.formState.errors.hasOwnProperty(type),
    [form.formState.errors]
  );

  const changeActiveTabToPrinters = () => {
    resetTableState();
    setActiveTab(EventTabsType.Printers);
  };

  return (
    <>
      <Form {...form}>
        <S3FileUploader
          config={filesConfig}
          onComplete={handleFormSubmit}
          refetchCredentials={refetchUploadCredentials}
        >
          {({
            retryUpload,
            filesState,
            isUploading,
            onFileChange,
            uploadFiles,
          }) => (
            <form onSubmit={form.handleSubmit(uploadFiles)}>
              {children({ isLoading: isUploading })}

              <div className="flex flex-col gap-10 max-w-7xl">
                <EventInfo />
                <AiConfiguration />
                <SecretKeys
                  eventId={uploadUrlData?.eventId}
                  isEditMode={editMode}
                />
                <VideoPreview
                  onRetry={retryUpload}
                  isUploading={isUploading}
                  filesState={filesState}
                  onFileChange={onFileChange}
                />

                <Tabs
                  defaultValue={EventTabsType.Models}
                  value={activeTab}
                  onValueChange={(tab) => setActiveTab(tab as EventTabsType)}
                >
                  <TabsList className="w-full justify-start">
                    <TabsTrigger
                      value={EventTabsType.Models}
                      onClick={resetTableState}
                      hasDot={checkTabError(EventTabsType.Models)}
                    >
                      Models
                    </TabsTrigger>
                    <TabsTrigger
                      value={EventTabsType.Styles}
                      onClick={resetTableState}
                      hasDot={checkTabError(EventTabsType.Styles)}
                    >
                      Styles
                    </TabsTrigger>
                    <TabsTrigger
                      value={EventTabsType.Masks}
                      onClick={resetTableState}
                      hasDot={checkTabError(EventTabsType.Masks)}
                    >
                      Masks
                    </TabsTrigger>
                    <TabsTrigger
                      value={EventTabsType.Printers}
                      onClick={resetTableState}
                      hasDot={checkTabError(EventTabsType.Printers)}
                    >
                      Printers
                    </TabsTrigger>
                    <TabsTrigger
                      value={EventTabsType.Participants}
                      onClick={resetTableState}
                      hasDot={checkTabError(EventTabsType.Participants)}
                    >
                      Participants
                    </TabsTrigger>
                    <TabsTrigger
                      value={EventTabsType.Inventory}
                      onClick={resetTableState}
                      hasDot={checkTabError(EventTabsType.Inventory)}
                    >
                      Inventory
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value={EventTabsType.Models}>
                    <ModelsSelection
                      onValidate={setShouldOpenWarningDialog}
                      defaultPrinters={defaultFormValues.printers}
                      defaultValue={defaultFormValues.models}
                      onRedirectToPrinters={changeActiveTabToPrinters}
                    />
                  </TabsContent>
                  <TabsContent value={EventTabsType.Styles}>
                    <StylesSelection defaultValue={defaultFormValues.styles} />
                  </TabsContent>
                  <TabsContent value={EventTabsType.Masks}>
                    <MaskSelection defaultValue={defaultFormValues.masks} />
                  </TabsContent>
                  <TabsContent value={EventTabsType.Printers}>
                    <PrintersSelection
                      defaultValue={defaultFormValues.printers}
                    />
                  </TabsContent>
                  <TabsContent value={EventTabsType.Participants}>
                    {uploadUrlData?.eventId && (
                      <Participants
                        editMode={editMode}
                        eventName={defaultValues?.name}
                        eventId={uploadUrlData?.eventId}
                      />
                    )}
                  </TabsContent>
                  <TabsContent value={EventTabsType.Inventory}>
                    {uploadUrlData?.eventId && (
                      <Inventory
                        editMode={editMode}
                        eventId={uploadUrlData?.eventId}
                      />
                    )}
                  </TabsContent>
                </Tabs>
              </div>
            </form>
          )}
        </S3FileUploader>
      </Form>

      {isSubmitWarningDialogOpen && (
        <CreateEventAlertDialog
          isOpen
          editMode={editMode}
          onSubmit={handleFormSubmit}
          onOpenChange={setIsSubmitWarningDialogOpen}
        />
      )}
    </>
  );
}
